import { CancelRounded, CheckCircleRounded, EditRounded, InfoRounded, InsertInvitationRounded, VerifiedRounded } from "@mui/icons-material";
import { Box, Chip, Grid, Popover, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField/TextField";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { Fragment, FunctionComponent, useEffect, useState} from "react";
import { Controller, useWatch } from "react-hook-form";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { cargosPlacesState } from "../../state/customerbaseState";
import { CBDocumentStatusValue, CBAttachmentFieldTypeNames, CBDossierKindKeys, IDossierFormValues, IShowFieldsParams, DOSSIER_ATTACHMENT_CONFIG_FIELD_TO_EXCLUDE, CBOmissionType, IBundleInfoValues, CBOrigin, CBOriginIdentifier, CBAuth } from "../../types";
import { getDocumentStatusChipColor } from "../../utils";
import { useHasScope } from "../../../../config/globalConfig";
import CBImage from "../CBImage";
import CBScoreCustomFields from "./attachment/CBScoreCustomFields";
import CBBundleInfo from "./attachment/bundleInfo/CBBundleInfo";

interface CustomerBaseAttachmentDetailProps {
    index: number,
    currentKind: string | null,
    numOfTypePages: number,
    fieldsLength: number,
    onSetEditing: () => void,
    //onRemove: () => void,
    onCheckDrivingLicence?: () => void,
    onUpdateStatus: (status: string) => void
}

const CustomerBaseAttachmentDetail: FunctionComponent<CustomerBaseAttachmentDetailProps> = ({
    index,
    currentKind,
    numOfTypePages,
    fieldsLength,
    onSetEditing,
    //onRemove,
    onCheckDrivingLicence,
    onUpdateStatus
}) => {

    const hasScope = useHasScope;
    const theme = useTheme();
    const { t } = useTranslation("customerbase");
    const dossierFormContext = useFormContext<IDossierFormValues>();
    const frontPageUri = dossierFormContext.getValues(`attachments.${index}.frontPage`);
    const backPageUri = dossierFormContext.getValues(`attachments.${index}.backPage`);
    const bundleInfo = dossierFormContext.getValues(`attachments.${index}.bundleInfo`);
    const origin = dossierFormContext.getValues(`attachments.${index}.origin`);
    const originIdentifier = dossierFormContext.getValues(`attachments.${index}.originIdentifier`);
    const isDrivingLicence = currentKind == CBDossierKindKeys.LICENCE;
    const currStatus = useWatch<IDossierFormValues, `attachments.${typeof index}.status`>({
        control: dossierFormContext.control,
        name: `attachments.${index}.status`});

    const cargosPlaceOptions = useRecoilValue(cargosPlacesState);

    const [showFields, setShowFields] = useState<IShowFieldsParams>({
        showIssueDate: true,
        showExpireDate: true,
        showBackPage: true,
        showAttachmentFields: true
    });

    const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [popoverContent, setPopoverContent] = useState<string>('');

    useEffect(() => {
        if(!currentKind) return;
        switch (currentKind) {
            case CBDossierKindKeys.LICENCE:
            case CBDossierKindKeys.IDENTITY_DOCUMENT_PASSPORT:
            case CBDossierKindKeys.IDENTITY_DOCUMENT_CARD:
            case CBDossierKindKeys.INTERNATIONAL_DRIVING_LICENCE:
            case CBDossierKindKeys.INTERNATIONAL_TRANSLATION:
                setShowFields({
                    showIssueDate: true,
                    showExpireDate: true,
                    showBackPage: numOfTypePages == 2,
                    showAttachmentFields: fieldsLength > 0
                });                                                        
                break;
            case CBDossierKindKeys.SELFIE:
                setShowFields({
                    showIssueDate: false,
                    showExpireDate: false,
                    showBackPage: numOfTypePages == 2,
                    showAttachmentFields: fieldsLength > 0
                });
                break;
            /*case CBDossierKindKeys.IDENTITY_DOCUMENT_PASSPORT:
                setShowFields({
                    showIssueDate: true,
                    showExpireDate: true,
                    showBackPage: numOfTypePages == 2,
                    showAttachmentFields: fieldsLength > 0
                });
                break;
            case CBDossierKindKeys.IDENTITY_DOCUMENT_CARD:
                setShowFields({
                    showIssueDate: true,
                    showExpireDate: true,
                    showBackPage: numOfTypePages == 2,
                    showAttachmentFields: fieldsLength > 0
                });
                break;*/
        
            default:
                setShowFields({
                    showIssueDate: true,
                    showExpireDate: true,
                    showBackPage: true,
                    showAttachmentFields: fieldsLength > 0
                });
                break;
        };

    }, [currentKind])

    const transformValue = (value: string | null, fieldType: string | null) => {
        let val: string | null = value || null;

        if(!!value && fieldType == CBAttachmentFieldTypeNames.MCTC_STATUS_TIMESTAMP) {
            var sp: string[] = value.split("T");
            if(sp.length > 1) {
                const s = sp[0].split("-");
                return s.reverse().join("/") + " " + sp[1];
            }
        }
            

        if(!!fieldType) {
            switch(fieldType){
                case CBAttachmentFieldTypeNames.ISSUE_DATE:
                    val = dayjs(value).isValid() ? dayjs(value).format("DD/MM/YYYY") : null;
                    break;
                case CBAttachmentFieldTypeNames.ISSUING_PLACE:
                    val = cargosPlaceOptions.find((cpo) => cpo.codice == value)?.descrizione || null;
                    break;
                case CBAttachmentFieldTypeNames.CITIZENSHIP:
                    val = cargosPlaceOptions.find((cpo) => cpo.codice == value)?.descrizione || null;
                    break;
                case CBAttachmentFieldTypeNames.MCTC_STATUS:
                    val = !!value ? t(`mctcStatus.${value}`) : t("labels.dataNotAvailable");
                    break;
                case CBAttachmentFieldTypeNames.MCTC_STATUS_CODE:
                    val = value || t("labels.dataNotAvailable");
                    break;           
                default:
                    val = val;
                    break;
            }
        }

        return val;
    }

    const chooseMCTCEndAdornment = (value: string | null, fieldType: string | null) => {
        let endAdornment = null;

        if(!!fieldType) {
            switch(fieldType){
                case CBAttachmentFieldTypeNames.MCTC_STATUS_TIMESTAMP:
                    endAdornment = <InsertInvitationRounded color="disabled"/>;
                    break;
                case CBAttachmentFieldTypeNames.MCTC_STATUS_MESSAGE:
                    endAdornment = !!value ? 
                        <Tooltip title={t("tooltips.document.showMessage")} arrow>
                            <IconButton 
                                color="info" 
                                onClick={(event) => {
                                    setPopoverContent(value);
                                    setPopoverAnchorEl(event.currentTarget);
                                }}>
                                <InfoRounded fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    : null;
                    break;
                default:
                    endAdornment = null;
            }
        }

        return endAdornment;
    }
    
    return (
        <Box
            border="1px solid #DBE4E9"
            boxShadow="0px 4px 4px rgba(0, 0, 0, 0.25)"
            borderRadius="4px"
            padding={theme.spacing(1) + ' ' + theme.spacing(2)}
            marginBottom={theme.spacing(3)} 
        >
            <Grid container spacing={2}>
                {<Grid item container md={12} xs={12} alignItems={"center"} mt={1}>
                    {<Grid item md={6} sm={6} xs={12}>
                        {<Stack direction={"row"} alignItems={"center"} columnGap={1}>
                            <Fragment>
                                <Controller 
                                    control={dossierFormContext.control}
                                    name={`attachments.${index}.type.identifier`}
                                    render={({field}) => (
                                        <Typography variant="h6">
                                            {t(`attachmentKindType.${field.value}`) || field.value}
                                        </Typography>
                                    )}
                                />
                            </Fragment>
                            <Fragment >
                                <Controller 
                                    control={dossierFormContext.control}
                                    name={`attachments.${index}.status`}
                                    render={({field}) => (
                                        <Chip
                                            label={t(`documentStatus.${field.value}`)}
                                            sx={{color: "white", backgroundColor: getDocumentStatusChipColor(field.value, theme)}}  
                                        />   
                                    )}
                                />
                            </Fragment> 
                        </Stack>}
                    </Grid>
                    }
                    {<Grid item md={6} sm={6} xs={12} >
                        <Stack direction={"row"} alignItems={"center"} justifyContent={"flex-end"} columnGap={1}>
                            {(origin?.toUpperCase() == CBOrigin.USER) && (originIdentifier?.toUpperCase() == CBOriginIdentifier.IDM) && !!bundleInfo &&
                                <CBBundleInfo bundleInfo={bundleInfo}/>
                            }
                            {hasScope(CBAuth.WRITE_CUSTOMER) && 
                                <Tooltip title={t("tooltips.document.validAsComplete")} arrow>
                                    <span>
                                        <IconButton
                                            color="accent"
                                            disabled={currStatus == CBDocumentStatusValue.COMPLETE} 
                                            onClick={() => onUpdateStatus(CBDocumentStatusValue.COMPLETE)}
                                        >
                                            <CheckCircleRounded fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            }
                            {hasScope(CBAuth.WRITE_CUSTOMER) && 
                                <Tooltip title={t("tooltips.document.validAsIncomplete")} arrow>
                                    <span>
                                        <IconButton
                                            color="accent"
                                            disabled={currStatus == CBDocumentStatusValue.INCOMPLETE}
                                            onClick={() => onUpdateStatus(CBDocumentStatusValue.INCOMPLETE)}
                                        >
                                            <CancelRounded fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            }
                            {hasScope(CBAuth.WRITE_CUSTOMER) && isDrivingLicence && onCheckDrivingLicence &&
                                <Tooltip title={t("tooltips.document.MCTC_check")} arrow>
                                    <IconButton color="accent" 
                                        onClick={onCheckDrivingLicence} 
                                    >
                                        <VerifiedRounded fontSize="small"/>
                                    </IconButton>
                                </Tooltip>
                            }
                            {hasScope(CBAuth.WRITE_CUSTOMER) && <IconButton color="accent" 
                                onClick={onSetEditing} 
                            >
                                <EditRounded fontSize="small"/>
                            </IconButton>
                            }
                            {/*<IconButton color={"accent"} onClick={onRemove}> 
                                <DeleteRounded fontSize="small" />
                        </IconButton>*/}
                        </Stack>
                    </Grid>
                    }
                </Grid>
                }
                <Grid item container id="detail-main-data-row" md={12} xs={12} columnSpacing={3} rowSpacing={4}>
                    
                    {(origin?.toUpperCase() == CBOrigin.USER) && (originIdentifier?.toUpperCase() == CBOriginIdentifier.IDM) &&
                        <Grid item xs={12}>
                            <CBScoreCustomFields 
                                attachmentIndex={index}
                                currentKind={currentKind}
                            />
                        </Grid>
                    }

                    {(currentKind !== CBDossierKindKeys.SELFIE) && 
                        <Grid item md={3} xs={12}>
                            <Controller
                                control={dossierFormContext.control}
                                name={`attachments.${index}.number`}
                                render={({field}) => (
                                    <TextField 
                                        fullWidth
                                        InputProps={{
                                            readOnly: true
                                        }}
                                        id={`idNumber-${index}`}
                                        label={t("idNumber")}
                                        value={field.value || ''}
                                    />
                                )}
                            />
                        </Grid>
                    }
                    {showFields.showIssueDate &&
                        <Grid item md={3} xs={12}>
                            <Controller
                                control={dossierFormContext.control}
                                name={`attachments.${index}.issueDate`}
                                render={({field}) => (
                                    <DatePicker
                                        {...field}
                                        readOnly
                                        label={t("releaseDate")}
                                        value={!!field.value ? dayjs(Number(field.value)) : null}
                                        inputFormat={"DD/MM/YYYY"}
                                        renderInput={(params: any) => (
                                            <TextField 
                                                id={`issueDate-${index}`}
                                                variant="standard"
                                                fullWidth
                                                label={t("releaseDate")}
                                                {...params}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                    }
                    {showFields.showExpireDate && 
                        <Grid item md={3} xs={12}>
                            <Controller
                                control={dossierFormContext.control}
                                name={`attachments.${index}.expireDate`}
                                render={({field}) => (
                                    <DatePicker
                                        {...field}
                                        readOnly
                                        label={t("expireDate")}
                                        value={!!field.value ? dayjs(Number(field.value)) : null}
                                        inputFormat={"DD/MM/YYYY"}
                                        renderInput={(params: any) => (
                                            <TextField 
                                                id={`expireDate-${index}`}
                                                variant="standard"
                                                fullWidth
                                                label={t("expireDate")}
                                                {...params}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                    }
                    {showFields.showAttachmentFields &&
                        
                        dossierFormContext.getValues(`attachments.${index}.fields`)?.map((f, fieldIndex) => (
                            (!DOSSIER_ATTACHMENT_CONFIG_FIELD_TO_EXCLUDE.includes(f.type?.name || '')) && <Grid item md={3} xs={12} key={f.type?.name}>
                                <Controller 
                                    control={dossierFormContext.control}
                                    name={`attachments.${index}.fields.${fieldIndex}.value`}
                                    render={({field}) => (
                                        <TextField 
                                            {...field}
                                            fullWidth
                                            InputProps={{
                                                readOnly: true,
                                                endAdornment: f.type?.name == CBAttachmentFieldTypeNames.ISSUE_DATE ? <InsertInvitationRounded color="disabled"/> : null
                                            }}
                                            id={`detail-cb-dossier-${f.type?.name}-${fieldIndex}`}
                                            label={isDrivingLicence && (f.type?.name == CBAttachmentFieldTypeNames.ISSUE_DATE) ? t("firstReleaseDate") : t(`${f.type?.name}`)}
                                            type="text"
                                            value={transformValue(field.value, f.type?.name) || CBOmissionType.OMISSION_DASH}
                                        />
                                    )}
                                />
                            </Grid>
                        ))
                    }
                    {showFields.showAttachmentFields &&
                        <Grid item container id="detail-field-data-row" md={12} xs={12} columnSpacing={3} rowSpacing={4}>
                            {
                                dossierFormContext.getValues(`attachments.${index}.fields`)?.map((f, fieldIndex) => (
                                    (f.type.name?.includes(CBAttachmentFieldTypeNames.MCTC)) && <Grid item md={3} xs={12} key={f.type?.name}>
                                        <Controller 
                                            control={dossierFormContext.control}
                                            name={`attachments.${index}.fields.${fieldIndex}.value`}
                                            render={({field}) => (
                                                <TextField 
                                                    {...field}
                                                    fullWidth
                                                    InputProps={{
                                                        readOnly: true,
                                                        endAdornment: chooseMCTCEndAdornment(field.value, f.type?.name)
                                                    }}
                                                    sx={ f.type?.name == CBAttachmentFieldTypeNames.MCTC_STATUS_MESSAGE ?  { "& .MuiInputBase-input" : {overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}} : {}}
                                                    id={`detail-cb-dossier-${f.type?.name}-${fieldIndex}`}
                                                    label={t(`${f.type?.name}`)}
                                                    type="text"
                                                    value={transformValue(field.value, f.type?.name) || CBOmissionType.OMISSION_DASH}
                                                />
                                            )}
                                        />
                                    </Grid>
                                ))
                            }
                        </Grid>
                    }
                </Grid>
                <Grid item container id="detail-file-data-row" md={12} xs={12} columnSpacing={3} rowSpacing={4} mb={1}>
                    <Grid item id="detail-first-file-row" md={showFields.showBackPage ? 6 : 12} xs={12}>
                        {!!frontPageUri &&
                            <CBImage 
                                imgBase64={frontPageUri} 
                                title={currentKind !== CBDossierKindKeys.SELFIE ? t("documentModal.frontPageTitle") : null}
                            />                       
                        }
                    </Grid>
                    {showFields.showBackPage &&
                        <Grid item id="last-file-row" md={6} xs={12}>
                            {!!backPageUri && 
                                <CBImage 
                                    imgBase64={backPageUri} 
                                    title={t("documentModal.backPageTitle")}
                                />
                            }
                        </Grid> 
                    }
                </Grid>
            </Grid>
            <Popover
                open={Boolean(popoverAnchorEl)}
                anchorEl={popoverAnchorEl}
                onClose={() => setPopoverAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
            >
                <Typography p={2}>{popoverContent}</Typography>
            </Popover> 
        </Box>
    );
}

export default CustomerBaseAttachmentDetail;